import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Copy, Loader2, Trash2, ExternalLink, ScrollText, RefreshCw, LineChart, RadioTower,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { RoleBadge, StatusBadge, HealthDot } from "@/components/Badges";
import { PageHeader } from "@/components/PageHeader";
import { getExchange } from "@/data/exchanges";
import { fmtUsd, fmtDate, relTime } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between px-4 py-3 border-b border-[#1c1c1c] last:border-0">
    <span className="text-xs text-[#737373]">{label}</span>
    <span className="text-sm text-[#F5F5F5]">{children}</span>
  </div>
);

export default function ExchangeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [forceDelete, setForceDelete] = useState(false);
  const [live, setLive] = useState(false);

  const { data: acc, isLoading } = useQuery({ queryKey: ["account", id], queryFn: async () => (await api.get(`/exchange-accounts/${id}`)).data });
  const { data: audit = [] } = useQuery({ queryKey: ["audit", id], queryFn: async () => (await api.get(`/exchange-accounts/${id}/audit`)).data });

  const patch = async (updates) => {
    try {
      await api.patch(`/exchange-accounts/${id}`, updates);
      qc.invalidateQueries({ queryKey: ["account", id] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Account updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const liveSync = async () => {
    setLive(true);
    try {
      const { data } = await api.post(`/exchange-accounts/${id}/live-sync`);
      qc.invalidateQueries({ queryKey: ["account", id] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["audit", id] });
      qc.invalidateQueries({ queryKey: ["pf-summary"] });
      qc.invalidateQueries({ queryKey: ["pf-accounts"] });
      data.ok ? toast.success(data.message) : toast.warning(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLive(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/exchange-accounts/${id}`, { params: forceDelete ? { force: true } : {} });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Account deleted");
      navigate("/exchanges");
    } catch (e) {
      const msg = formatApiError(e);
      if (e?.response?.status === 409) { setForceDelete(true); toast.error(msg + " Toggle force-delete to remove connected accounts too."); }
      else toast.error(msg);
    }
  };

  if (isLoading || !acc) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>;

  const ex = getExchange(acc.exchange_slug);
  const displayStatus = acc.connection_status === "unverified" ? "unverified" : acc.status;

  return (
    <div>
      <button onClick={() => navigate("/exchanges")} data-testid="detail-back-btn" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#F5F5F5] mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to My Exchanges
      </button>

      <PageHeader
        title={acc.account_name}
        subtitle={acc.description}
        pageKey="exchange-detail"
        testid="detail-header"
        actions={
          <div className="flex items-center gap-2">
            {displayStatus === "unverified" && (
              <button onClick={liveSync} disabled={live} data-testid="detail-live-sync-btn"
                className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] disabled:opacity-60 text-[#0A0A0A] font-semibold text-sm rounded-sm px-4 py-2 transition-colors">
                <RadioTower className={cn("w-4 h-4", live && "animate-pulse")} /> {live ? "Syncing…" : "Live Sync"}
              </button>
            )}
            <button onClick={() => navigate(`/portfolio/${id}`)} data-testid="detail-stats-btn"
              className="flex items-center gap-2 border border-[#333333] hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F5F5F5] text-sm rounded-sm px-4 py-2 transition-colors">
              <LineChart className="w-4 h-4" /> Stats & Reports
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="detail-delete-btn" className="flex items-center gap-2 border border-[rgba(239,68,68,0.3)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)] text-sm rounded-sm px-4 py-2 transition-colors">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#141414] border-[#262626]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[#F5F5F5]">Delete account?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#A3A3A3]">
                    This permanently removes <span className="text-[#F5F5F5]">{acc.account_name}</span>.
                    {acc.account_role === "master" && (acc.copy_count ?? 0) > 0 && (
                      <span className="block mt-3 text-[#F59E0B]">This master has {acc.copy_count} connected account(s).</span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {acc.account_role === "master" && (acc.copy_count ?? 0) > 0 && (
                  <label className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                    <Switch checked={forceDelete} onCheckedChange={setForceDelete} data-testid="force-delete-switch" />
                    Force delete (also removes connected accounts)
                  </label>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-[#333333] text-[#F5F5F5] hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del} data-testid="confirm-delete-btn" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />

      {displayStatus === "unverified" && (
        <div className="mb-4 rounded-md border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.06)] px-4 py-3 flex items-start gap-3" data-testid="unverified-banner">
          <RadioTower className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-[#F59E0B]">Account is Unverified — pending Live Region Sync</div>
            <p className="text-xs text-[#A3A3A3] mt-0.5">The connection test was region-restricted. Run Live Sync (or deploy to an allowed region) to auto-verify and pull the real balance.</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-md border border-[#262626] bg-[#141414]">
            <div className="flex items-center gap-3 px-4 py-4 border-b border-[#262626]">
              <span className="w-11 h-11 rounded-md bg-[#0A0A0A] border border-[#262626] flex items-center justify-center font-heading font-bold" style={{ color: ex.color }}>{ex.name.slice(0, 2).toUpperCase()}</span>
              <div>
                <div className="text-sm font-medium text-[#F5F5F5] flex items-center gap-2">{ex.name} <RoleBadge role={acc.account_role} /></div>
                <div className="text-xs text-[#737373] font-mono">{acc.api_key_masked}</div>
              </div>
              <div className="ml-auto"><StatusBadge status={displayStatus} /></div>
            </div>
            <Row label="Balance">{acc.balance != null ? <span className="font-mono">{fmtUsd(acc.balance)}</span> : "—"}</Row>
            <Row label="Market"><span className="capitalize">{acc.market}</span></Row>
            <Row label="Relationship">
              {acc.account_role === "master"
                ? <span className="flex items-center gap-1.5"><Copy className="w-3.5 h-3.5 text-[#525252]" />{acc.copy_count ?? 0} connected</span>
                : <span className="flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-[#D4AF37]" />{acc.master_name || "—"}</span>}
            </Row>
            <Row label="Demo account">{acc.is_demo ? "Yes" : "No"}</Row>
            <Row label="Last sync">{relTime(acc.last_sync_at)}</Row>
            <Row label="Verification"><span className="flex items-center gap-2"><HealthDot status={displayStatus} />{acc.last_verified_at ? `Verified ${relTime(acc.last_verified_at)}` : "Not verified"}</span></Row>
            <Row label="Created">{fmtDate(acc.created_at)}</Row>
          </div>

          <div className="rounded-md border border-[#262626] bg-[#141414]">
            <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
              <span className="text-sm font-medium text-[#F5F5F5]">Permissions</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#1c1c1c]">
              <span className="text-sm text-[#A3A3A3]">Trading enabled</span>
              <Switch checked={acc.trading_enabled} onCheckedChange={(v) => patch({ trading_enabled: v })} data-testid="toggle-trading" />
            </div>
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-sm text-[#A3A3A3]">Withdrawals enabled</span>
              <Switch checked={acc.withdrawal_enabled} onCheckedChange={(v) => patch({ withdrawal_enabled: v })} data-testid="toggle-withdrawal" />
            </div>
          </div>

          {acc.account_role === "master" && acc.linked_copies?.length > 0 && (
            <div className="rounded-md border border-[#262626] bg-[#141414]">
              <div className="px-4 py-3 border-b border-[#262626] text-sm font-medium text-[#F5F5F5]">Connected accounts</div>
              {acc.linked_copies.map((c) => (
                <button key={c.id} onClick={() => navigate(`/exchanges/${c.id}`)} data-testid={`linked-copy-${c.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A] transition-colors text-left">
                  <div className="flex items-center gap-2">
                    <Copy className="w-3.5 h-3.5 text-[#525252]" />
                    <span className="text-sm text-[#F5F5F5]">{c.account_name}</span>
                  </div>
                  <span className="font-mono text-sm text-[#A3A3A3]">{fmtUsd(c.balance)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-md border border-[#262626] bg-[#141414]">
          <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-medium text-[#F5F5F5]">Audit trail</span>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {audit.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#737373]">No activity recorded yet.</div>
            ) : audit.map((a) => (
              <div key={a.id} className="px-4 py-3 border-b border-[#1c1c1c] last:border-0" data-testid={`audit-${a.id}`}>
                <div className="flex items-center gap-2">
                  <span className={cn("w-1.5 h-1.5 rounded-full", a.metadata?.ok === false ? "bg-[#EF4444]" : "bg-[#10B981]")} />
                  <span className="text-sm text-[#F5F5F5]">{a.action}</span>
                </div>
                <div className="text-[11px] text-[#737373] mt-1 font-mono">{fmtDate(a.timestamp)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
